import { request, type GameSocket } from "@unfaithful/client";
import type { VoiceIceServer, VoicePeer } from "@unfaithful/shared";

/**
 * THE VOICE LINE — live audio between phones, for guests who aren't in the
 * room, plus one phone in the room (in the middle of the table) so the room
 * can hear them and they can hear the room.
 *
 * Phones talk to each other directly (WebRTC); the castle server only passes
 * the connection messages along. The newcomer calls everyone already on the
 * line, so two phones never call each other at once. Muted at night.
 */
export interface VoiceState {
  joined: boolean;
  connecting: boolean;
  muted: boolean;
  /** Forced quiet (night). */
  hushed: boolean;
  peers: { id: string; name: string; state: RTCPeerConnectionState | "new" }[];
  error: string | null;
}

type Signal = { kind: "offer" | "answer"; sdp: RTCSessionDescriptionInit } | { kind: "ice"; candidate: RTCIceCandidateInit };

export class VoiceLine {
  private socket: GameSocket | null = null;
  private stream: MediaStream | null = null;
  private ice: VoiceIceServer[] = [];
  private readonly pcs = new Map<string, { pc: RTCPeerConnection; name: string; audio: HTMLAudioElement }>();
  private state: VoiceState = { joined: false, connecting: false, muted: false, hushed: false, peers: [], error: null };
  private listeners = new Set<(s: VoiceState) => void>();
  private offs: (() => void)[] = [];

  subscribe(fn: (s: VoiceState) => void): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private emit(patch: Partial<VoiceState> = {}): void {
    this.state = {
      ...this.state,
      ...patch,
      peers: [...this.pcs].map(([id, p]) => ({ id, name: p.name, state: p.pc.connectionState })),
    };
    for (const fn of this.listeners) fn(this.state);
  }

  get joined(): boolean {
    return this.state.joined;
  }

  /** After the phone's connection drops and returns, its id has changed: join again. */
  async rejoin(socket: GameSocket): Promise<void> {
    if (!this.state.joined) return;
    const muted = this.state.muted;
    this.leave();
    await this.join(socket);
    this.setMuted(muted);
  }

  async join(socket: GameSocket): Promise<void> {
    if (this.state.joined || this.state.connecting) return;
    this.socket = socket;
    this.emit({ connecting: true, error: null });
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch {
      this.emit({ connecting: false, error: "The microphone is blocked. Allow it in your browser settings to join the voice line." });
      return;
    }
    // Someone new: they'll call us; get ready to answer, under their name.
    const onJoined = (p: VoicePeer) => {
      this.pcFor(p.id, p.name);
    };
    const onLeft = (id: string) => this.drop(id);
    const onSignal = (m: { from: string; data: unknown }) => void this.onSignal(m.from, m.data as Signal);
    socket.on("voice:peer-joined", onJoined);
    socket.on("voice:peer-left", onLeft);
    socket.on("voice:signal", onSignal);
    this.offs = [() => socket.off("voice:peer-joined", onJoined), () => socket.off("voice:peer-left", onLeft), () => socket.off("voice:signal", onSignal)];
    try {
      const res = await request(socket, "voice:join");
      this.ice = res.iceServers;
      this.applyMute();
      // We're the newcomer: call everyone already on the line.
      for (const p of res.peers) await this.call(p.id, p.name);
      this.emit({ joined: true, connecting: false });
    } catch {
      this.leave();
      this.emit({ error: "Couldn't join the voice line. Try again." });
    }
  }

  leave(): void {
    if (this.socket?.connected) void request(this.socket, "voice:leave").catch(() => undefined);
    for (const id of [...this.pcs.keys()]) this.drop(id);
    this.offs.forEach((f) => f());
    this.offs = [];
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.emit({ joined: false, connecting: false });
  }

  setMuted(muted: boolean): void {
    this.state.muted = muted;
    this.applyMute();
    this.emit({ muted });
  }

  /** Night: everyone on the line goes quiet, and hears nothing. */
  setHushed(hushed: boolean): void {
    if (this.state.hushed === hushed) return;
    this.state.hushed = hushed;
    this.applyMute();
    for (const p of this.pcs.values()) p.audio.muted = hushed;
    this.emit({ hushed });
  }

  private applyMute(): void {
    const on = !this.state.muted && !this.state.hushed;
    this.stream?.getAudioTracks().forEach((t) => (t.enabled = on));
  }

  private pcFor(id: string, name: string): RTCPeerConnection {
    const have = this.pcs.get(id);
    if (have) return have.pc;
    const pc = new RTCPeerConnection({ iceServers: this.ice as RTCIceServer[] });
    const audio = new Audio();
    audio.autoplay = true;
    audio.muted = this.state.hushed;
    this.pcs.set(id, { pc, name, audio });
    this.stream?.getTracks().forEach((t) => pc.addTrack(t, this.stream!));
    pc.ontrack = (e) => {
      audio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
      void audio.play().catch(() => undefined);
    };
    pc.onicecandidate = (e) => e.candidate && this.send(id, { kind: "ice", candidate: e.candidate.toJSON() });
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") pc.restartIce();
      this.emit();
    };
    this.emit();
    return pc;
  }

  private async call(id: string, name: string): Promise<void> {
    const pc = this.pcFor(id, name);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    this.send(id, { kind: "offer", sdp: pc.localDescription!.toJSON() });
  }

  private async onSignal(from: string, s: Signal): Promise<void> {
    const pc = this.pcFor(from, this.pcs.get(from)?.name ?? "Someone");
    try {
      if (s.kind === "offer") {
        await pc.setRemoteDescription(s.sdp);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.send(from, { kind: "answer", sdp: pc.localDescription!.toJSON() });
      } else if (s.kind === "answer") {
        await pc.setRemoteDescription(s.sdp);
      } else if (s.kind === "ice") {
        await pc.addIceCandidate(s.candidate).catch(() => undefined);
      }
    } catch {
      /* a stale message from a dropped connection */
    }
  }

  private send(to: string, data: Signal): void {
    this.socket?.emit("voice:signal", { to, data });
  }

  private drop(id: string): void {
    const p = this.pcs.get(id);
    if (!p) return;
    p.pc.close();
    p.audio.srcObject = null;
    this.pcs.delete(id);
    this.emit();
  }
}

export const voiceLine = new VoiceLine();
